(() => {
  const ROOT_SELECTOR = '[data-shq-builder]';

  class SugarHQBundleBuilder {
    constructor(root) {
      this.root = root;
      this.minCount = Math.max(1, Number(root.dataset.minCount || 1));
      this.maxCount = Math.max(this.minCount, Number(root.dataset.maxCount || this.minCount));
      this.bundleTitle = root.dataset.bundleTitle || 'Build Your Own Box';
      this.bundleHandle = root.dataset.bundleHandle || '';
      this.redirectToCart = root.dataset.cartRedirect !== 'false';
      this.pricingMode = root.dataset.pricingMode || 'bundle_offer';
      this.bundlePrice = Number(root.dataset.bundlePrice || 0);
      this.currencyCode = root.dataset.currencyCode || 'AUD';
      this.allowMultiples = root.dataset.allowMultiples !== 'false';
      this.showLineTotals = root.dataset.showLineTotals !== 'false';
      this.isFixedBuilder = this.minCount === this.maxCount;
      this.selected = [];
      this.isAdding = false;

      this.cards = Array.from(root.querySelectorAll('[data-shq-card]'));
      this.tabs = Array.from(root.querySelectorAll('[data-shq-tab]'));
      this.panels = Array.from(root.querySelectorAll('[data-shq-panel]'));
      this.search = root.querySelector('[data-shq-search]');
      this.count = root.querySelector('[data-shq-count]');
      this.list = root.querySelector('[data-shq-selected-list]');
      this.addButton = root.querySelector('[data-shq-add]');
      this.clearButton = root.querySelector('[data-shq-clear]');
      this.helper = root.querySelector('[data-shq-helper]');
      this.message = root.querySelector('[data-shq-message]');
      this.noResults = root.querySelector('[data-shq-no-results]');
      this.progressDots = Array.from(root.querySelectorAll('[data-shq-progress-dot]'));
      this.total = root.querySelector('[data-shq-total]');
      this.heroTotal = root.querySelector('[data-shq-hero-total]');
      this.selector = root.querySelector('.shq-builder__selector');

      this.bindEvents();
      this.render();
    }

    bindEvents() {
      this.cards.forEach((card) => {
        const button = card.querySelector('[data-shq-select]');
        if (!button) return;
        button.addEventListener('click', () => this.toggleCard(card));
      });

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.activateTab(tab.dataset.shqTab));
      });

      if (this.search) {
        this.search.addEventListener('input', () => this.filterCards(this.search.value));
      }

      if (this.clearButton) this.clearButton.addEventListener('click', () => this.clear());
      if (this.addButton) this.addButton.addEventListener('click', () => this.addToCart());

      if (this.list) {
        this.list.addEventListener('click', (event) => this.handleSummaryNavigation(event));
        this.list.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          const target = event.target.closest('[data-shq-guide]');
          if (!target) return;
          event.preventDefault();
          this.navigateFromSummary(target);
        });
      }
    }

    handleSummaryNavigation(event) {
      if (event.target.closest('[data-quantity-action], [data-remove-variant]')) return;
      const target = event.target.closest('[data-shq-guide]');
      if (!target) return;
      this.navigateFromSummary(target);
    }

    navigateFromSummary(target) {
      const variantId = Number(target.dataset.guideVariant || 0);
      if (variantId) {
        const card = this.cards.find((item) => Number(item.dataset.variantId) === variantId);
        if (card) {
          this.revealCard(card);
          return;
        }
      }
      this.revealSelector();
    }

    revealSelector() {
      if (!this.selector) return;
      this.selector.scrollIntoView({ behavior: 'smooth', block: 'start' });
      this.pulseElement(this.selector);

      window.setTimeout(() => {
        if (this.search && !this.search.disabled) {
          this.search.focus({ preventScroll: true });
          return;
        }
        const firstButton = this.cards
          .map((card) => card.querySelector('[data-shq-select]'))
          .find((button) => button && !button.disabled && button.offsetParent !== null);
        firstButton?.focus({ preventScroll: true });
      }, 550);
    }

    revealCard(card) {
      const panel = card.closest('[data-shq-panel]');
      if (panel?.dataset.shqPanel) this.activateTab(panel.dataset.shqPanel);

      if (this.search?.value) {
        this.search.value = '';
        this.filterCards('');
      }

      window.setTimeout(() => {
        card.scrollIntoView({ behavior: 'smooth', block: 'center' });
        this.pulseElement(card);
        const button = card.querySelector('[data-shq-select]');
        window.setTimeout(() => button?.focus({ preventScroll: true }), 500);
      }, 50);
    }

    pulseElement(element) {
      if (!element || typeof element.animate !== 'function') return;
      element.animate(
        [
          { boxShadow: '0 0 0 0 rgba(245, 39, 122, 0)' },
          { boxShadow: '0 0 0 7px rgba(245, 39, 122, 0.18)' },
          { boxShadow: '0 0 0 0 rgba(245, 39, 122, 0)' },
        ],
        { duration: 850, easing: 'ease-out' }
      );
    }

    cardData(card) {
      return {
        variantId: Number(card.dataset.variantId),
        productId: Number(card.dataset.productId),
        productHandle: card.dataset.productHandle || '',
        title: card.dataset.productTitle || '',
        productVendor: card.dataset.productVendor || '',
        productType: card.dataset.productType || '',
        variantTitle: card.dataset.variantTitle || '',
        sku: card.dataset.variantSku || '',
        option1: card.dataset.option1 || '',
        option2: card.dataset.option2 || '',
        option3: card.dataset.option3 || '',
        image: card.dataset.image || '',
        price: Number(card.dataset.price || 0),
        compareAtPrice: Number(card.dataset.compareAtPrice || 0),
        available: card.dataset.available === 'true',
        inventoryQuantity: Number(card.dataset.inventoryQuantity || 0),
        inventoryManagement: card.dataset.inventoryManagement || '',
        inventoryPolicy: card.dataset.inventoryPolicy || 'deny',
        card,
      };
    }

    toggleCard(card) {
      const data = this.cardData(card);
      if (!data.available || this.isAdding) return;

      const line = this.selected.find((item) => item.variantId === data.variantId);
      const selectedCount = this.selectedCount();

      if (this.allowMultiples) {
        if (selectedCount >= this.maxCount) {
          this.showMaximumMessage();
          return;
        }

        if (line) {
          line.quantity += 1;
        } else {
          this.selected.push({ ...data, quantity: 1 });
        }
      } else if (line) {
        this.selected = this.selected.filter((item) => item.variantId !== data.variantId);
      } else {
        if (selectedCount >= this.maxCount) {
          this.showMaximumMessage();
          return;
        }
        this.selected.push({ ...data, quantity: 1 });
      }

      this.hideMessage();
      this.render();
    }

    showMaximumMessage() {
      this.showMessage(
        `Maximum reached. Your bundle can include up to ${this.maxCount} item${this.maxCount === 1 ? '' : 's'}. Reduce a quantity or remove a flavour to choose another.`,
        'notice'
      );
      this.root.querySelector('[data-shq-summary]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    activateTab(id) {
      this.tabs.forEach((tab) => {
        const active = tab.dataset.shqTab === id;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', String(active));
      });
      this.panels.forEach((panel) => {
        const active = panel.dataset.shqPanel === id;
        panel.classList.toggle('is-active', active);
        panel.hidden = !active;
      });
      if (this.search?.value) this.filterCards(this.search.value);
    }

    filterCards(value) {
      const query = value.trim().toLowerCase();
      let matches = 0;
      this.cards.forEach((card) => {
        const show = !query || (card.dataset.title || '').includes(query);
        card.hidden = !show;
        if (show) matches += 1;
      });
      if (this.noResults) this.noResults.hidden = matches !== 0;
      if (query) {
        this.panels.forEach((panel) => { panel.hidden = false; panel.classList.add('is-active'); });
        this.tabs.forEach((tab) => tab.classList.remove('is-active'));
      } else if (this.tabs[0]) {
        const active = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.tabs[0];
        this.activateTab(active.dataset.shqTab);
      }
    }

    clear() {
      this.selected = [];
      this.hideMessage();
      this.render();
    }

    remove(variantId) {
      this.selected = this.selected.filter((item) => item.variantId !== variantId);
      this.hideMessage();
      this.render();
    }

    changeQuantity(variantId, change) {
      const line = this.selected.find((item) => item.variantId === variantId);
      if (!line || this.isAdding) return;

      if (change > 0 && this.selectedCount() >= this.maxCount) {
        this.showMaximumMessage();
        return;
      }

      line.quantity += change;
      if (line.quantity <= 0) {
        this.remove(variantId);
        return;
      }

      this.hideMessage();
      this.render();
    }

    selectedCount() {
      return this.selected.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    }

    selectedTotal() {
      return this.selected.reduce(
        (sum, item) => sum + (Number(item.price || 0) * Number(item.quantity || 0)),
        0
      );
    }

    formatMoney(cents) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-AU', {
          style: 'currency',
          currency: this.currencyCode,
        }).format(Number(cents || 0) / 100);
      } catch (error) {
        return `$${(Number(cents || 0) / 100).toFixed(2)}`;
      }
    }

    renderPrice() {
      if (this.pricingMode !== 'live_total') return;
      const formatted = this.formatMoney(this.selectedTotal());
      if (this.total) this.total.textContent = formatted;
      if (this.heroTotal) this.heroTotal.textContent = formatted;
    }

    render() {
      const selectedCount = this.selectedCount();
      const quantityByVariant = new Map(
        this.selected.map((item) => [item.variantId, Number(item.quantity || 0)])
      );

      this.cards.forEach((card) => {
        const variantId = Number(card.dataset.variantId);
        const quantity = quantityByVariant.get(variantId) || 0;
        const isSelected = quantity > 0;
        card.classList.toggle('is-selected', isSelected);

        const button = card.querySelector('[data-shq-select]');
        const label = card.querySelector('.shq-builder__select-label');
        const badge = card.querySelector('[data-shq-card-quantity]');

        if (button) button.setAttribute('aria-pressed', String(isSelected));

        if (label && card.dataset.available === 'true') {
          if (!isSelected) {
            label.textContent = 'Select';
          } else if (this.allowMultiples) {
            label.textContent = 'Add another';
          } else {
            label.textContent = 'Selected';
          }
        }

        if (badge) {
          badge.hidden = !isSelected;
          badge.textContent = `×${quantity}`;
          badge.setAttribute('aria-label', `${quantity} selected`);
        }
      });

      let qualificationState = 'qualifying';
      if (selectedCount >= this.maxCount) {
        qualificationState = this.isFixedBuilder ? 'complete' : 'maximum';
      } else if (selectedCount >= this.minCount) {
        qualificationState = 'ready';
      }
      this.root.dataset.qualificationState = qualificationState;

      if (this.count) {
        if (selectedCount < this.minCount) {
          const remaining = this.minCount - selectedCount;
          const selectedLabel = `${selectedCount} flavour${selectedCount === 1 ? '' : 's'} selected`;
          this.count.textContent = selectedCount === 0
            ? `${selectedLabel} · Choose ${this.minCount}`
            : `${selectedLabel} · Choose ${remaining} more`;
        } else if (this.isFixedBuilder) {
          this.count.textContent = `✓ Bundle complete · ${selectedCount} of ${this.maxCount} selected`;
        } else if (selectedCount >= this.maxCount) {
          this.count.textContent = `Maximum reached · ${selectedCount} flavours selected`;
        } else {
          this.count.textContent = `✓ Ready to add to cart · ${selectedCount} flavour${selectedCount === 1 ? '' : 's'} selected`;
        }
      }

      this.progressDots.forEach((dot, index) => {
        dot.classList.toggle('is-filled', index < Math.min(selectedCount, this.minCount));
      });

      if (this.list) {
        this.list.innerHTML = '';

        this.selected.forEach((item) => {
          const row = document.createElement('div');
          row.className = 'shq-builder__selected-item shq-builder__selected-item--compact';
          row.dataset.shqGuide = 'selected';
          row.dataset.guideVariant = String(item.variantId);
          row.setAttribute('role', 'group');
          row.setAttribute('tabindex', '0');
          row.setAttribute('aria-label', `${item.title}, quantity ${item.quantity}. Activate to find this flavour.`);
          row.style.cursor = 'pointer';

          const lineTotal = Number(item.price || 0) * Number(item.quantity || 0);
          const showLineTotal = this.pricingMode === 'live_total' && this.showLineTotals;

          row.innerHTML = `
            ${item.image ? `<img src="${this.escapeHtml(item.image)}" alt="" width="52" height="52" loading="lazy">` : '<span aria-hidden="true"></span>'}
            <div class="shq-builder__selected-copy">
              <strong>${this.escapeHtml(item.title)}</strong>
              <small>${this.escapeHtml(item.variantTitle)}</small>
              ${showLineTotal ? `<small class="shq-builder__line-total">${this.formatMoney(lineTotal)}</small>` : ''}
            </div>
            <div class="shq-builder__quantity-controls" aria-label="Quantity for ${this.escapeHtml(item.title)}">
              <button type="button" data-quantity-action="decrease" aria-label="Decrease ${this.escapeHtml(item.title)} quantity">−</button>
              <span class="shq-builder__quantity-value" aria-live="polite">${item.quantity}</span>
              <button type="button" data-quantity-action="increase" aria-label="Increase ${this.escapeHtml(item.title)} quantity" ${selectedCount >= this.maxCount ? 'disabled' : ''}>+</button>
            </div>
          `;

          row.querySelector('[data-quantity-action="decrease"]')
            ?.addEventListener('click', () => this.changeQuantity(item.variantId, -1));
          row.querySelector('[data-quantity-action="increase"]')
            ?.addEventListener('click', () => this.changeQuantity(item.variantId, 1));

          this.list.appendChild(row);
        });

        const remainingToMinimum = Math.max(0, this.minCount - selectedCount);
        const remainingToMaximum = Math.max(0, this.maxCount - selectedCount);

        if (remainingToMaximum > 0) {
          const prompt = document.createElement('div');
          prompt.className = 'shq-builder__empty-slot shq-builder__compact-prompt';
          prompt.dataset.shqGuide = 'empty';
          prompt.setAttribute('role', 'button');
          prompt.setAttribute('tabindex', '0');
          prompt.style.cursor = 'pointer';

          if (remainingToMinimum > 0) {
            prompt.setAttribute('aria-label', `Choose ${remainingToMinimum} more item${remainingToMinimum === 1 ? '' : 's'}`);
            prompt.innerHTML = `<span>+</span><p>Choose ${remainingToMinimum} more flavour${remainingToMinimum === 1 ? '' : 's'}</p>`;
          } else {
            prompt.setAttribute('aria-label', `Add up to ${remainingToMaximum} more item${remainingToMaximum === 1 ? '' : 's'}`);
            prompt.innerHTML = `<span>+</span><p>Add up to ${remainingToMaximum} more flavour${remainingToMaximum === 1 ? '' : 's'}</p>`;
          }

          this.list.appendChild(prompt);
        }
      }

      const ready = selectedCount >= this.minCount && selectedCount <= this.maxCount;
      const remainingToMinimum = Math.max(0, this.minCount - selectedCount);
      const remainingToMaximum = Math.max(0, this.maxCount - selectedCount);

      if (this.addButton) {
        this.addButton.disabled = !ready || this.isAdding;
        if (!this.isAdding) {
          this.addButton.textContent = ready
            ? (this.addButton.dataset.originalText || 'Add bundle to cart')
            : `Choose ${remainingToMinimum} more flavour${remainingToMinimum === 1 ? '' : 's'}`;
        }
      }

      if (this.clearButton) this.clearButton.hidden = selectedCount === 0;

      if (this.helper) {
        if (selectedCount < this.minCount) {
          this.helper.textContent = this.isFixedBuilder
            ? `Choose ${remainingToMinimum} more flavour${remainingToMinimum === 1 ? '' : 's'} to complete your bundle.`
            : `Select ${remainingToMinimum} more flavour${remainingToMinimum === 1 ? '' : 's'} to continue.`;
        } else if (this.isFixedBuilder) {
          this.helper.textContent = 'Your bundle is complete and ready to add to cart.';
        } else if (selectedCount >= this.maxCount) {
          this.helper.textContent = 'Maximum reached. Your bundle is ready.';
        } else {
          this.helper.textContent = 'Keep adding favourites or add your bundle to cart.';
        }
      }

      this.renderPrice();
    }

    async addToCart() {
      const selectedCount = this.selectedCount();
      if (selectedCount < this.minCount || selectedCount > this.maxCount || this.isAdding) return;
      this.isAdding = true;
      this.render();
      this.addButton.textContent = 'Adding your box…';
      this.hideMessage();

      const bundleId = `SHQ-${Date.now()}-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;
      const items = this.selected.map((item, index) => ({
        id: item.variantId,
        quantity: item.quantity,
        properties: {
          'Box': this.bundleTitle,
          'Box selection': `${index + 1} of ${this.selected.length} flavours`,
          'Box quantity': String(item.quantity),
          'Total bundle items': String(selectedCount),
          '_Sugar HQ Minimum Selections': String(this.minCount),
          '_Sugar HQ Maximum Selections': String(this.maxCount),
          '_Sugar HQ Box ID': bundleId,
          '_Sugar HQ Bundle': this.bundleHandle,
          '_Sugar HQ Pricing Mode': this.pricingMode,
          '_Sugar HQ Variant SKU': item.sku || '',
          '_Sugar HQ Variant Title': item.variantTitle || '',
          '_Sugar HQ Product Handle': item.productHandle || '',
        },
      }));

      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items }),
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || 'We could not add the box to your cart.');

        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

        if (this.redirectToCart) {
          window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
        } else {
          this.showMessage('Your custom box has been added to the cart.', 'success');
          this.selected = [];
        }
      } catch (error) {
        this.showMessage(error.message || 'Something went wrong. Please try again.', 'error');
      } finally {
        this.isAdding = false;
        this.render();
      }
    }

    showMessage(text, type) {
      if (!this.message) return;
      this.message.hidden = false;
      this.message.textContent = text;
      this.message.dataset.type = type;
    }

    hideMessage() {
      if (!this.message) return;
      this.message.hidden = true;
      this.message.textContent = '';
      delete this.message.dataset.type;
    }

    escapeHtml(value) {
      return String(value).replace(/[&<>'"]/g, (character) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#039;', '"': '&quot;',
      })[character]);
    }
  }

  function init(scope = document) {
    scope.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      if (root.dataset.shqInitialised === 'true') return;
      root.dataset.shqInitialised = 'true';
      const button = root.querySelector('[data-shq-add]');
      if (button) button.dataset.originalText = button.textContent.trim();
      new SugarHQBundleBuilder(root);
    });
  }

  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
